/**
 * Sieve-analysis CAPTURE wiring — the single source of truth shared by the
 * Data-entry capture form, the Reports read-only sheet, and the sieve-shaker
 * provisioning step, so that tag names, reading metrics, computed-tag input
 * aliases, and `classifySieveTag` recognition can never drift apart.
 *
 * A sieve shaker carries 12 per-mesh retained-mass datapoints (`source='manual'`
 * tags, unit g) plus one GFN **computed tag** derived from them. The mesh masses
 * are hand-keyed on the Data-entry page and published as readings; the GFN is
 * recomputed by the computed-tag worker whenever the masses change.
 *
 * Two invariants this module guarantees:
 *   1. Every mesh tag name is `classifySieveTag`-recognisable AND per-entity
 *      unique (a disc suffix) — because every manual tag on a plant shares that
 *      plant's ONE manual-entry gateway Thing (per-plant since migration 0089,
 *      `twin/manual_tags.go` ensureManualGatewayForPlant) and readings key on
 *      (device_slug, metric=name), so two shakers with identically named mesh tags
 *      would collide on one series. The mesh token leads the name so
 *      classifySieveTag matches it first.
 *   2. A saved test always publishes ALL provisioned meshes, zero-filled — so the
 *      GFN computed tag never NaNs out from a missing input.
 */

import { AFS_US_SERIES } from "@/lib/qc/gfn";
import { classifySieveTag } from "@/lib/qc/sieve-sheet";
import type { ReadingInput } from "@/lib/api/thing-readings";
import type { ComputedTagInput } from "@/lib/compute/types";
import type { ComputedTagCreateBody } from "@/lib/api/computed-tags";

/** Unit + data_kind every mesh retained-mass datapoint is provisioned with. */
export const SIEVE_MASS_UNIT = "g";
export const SIEVE_MASS_DATA_KIND = "analog_scalar";

/** Output unit + kind for the derived GFN computed tag. */
export const GFN_OUTPUT_UNIT = "AFS";
export const GFN_OUTPUT_DATA_KIND = "derived_kpi";

/** math.js-safe input alias for a mesh (m6 … m270, mpan). */
export function meshAlias(meshDesignation: string): string {
  return `m${meshDesignation}`;
}

/**
 * Symbols the disc is rendered in: digits + consonants, no vowels, no `l`.
 *
 * Vowel-free is what makes `classifySieveTag` safety STRUCTURAL rather than
 * probabilistic — every token that grammar looks for (`pan`, `mic`, `micron`,
 * `um`, `mesh`, `sieve`, `afs no`, `fineness`) contains a vowel, so a disc can
 * never spell one. Digits stay legal because the mesh regex anchors on a leading
 * `sieve|mesh|#` token and `String.match` returns the LEFTMOST match, which is
 * always the "Sieve <n>" label at index 0 of the name.
 */
const DISC_ALPHABET = "0123456789bcdfghjkmnpqrstvwxyz"; // 30 symbols
const DISC_LEN = 7; // 30^7 ≈ 2.19e10, comfortably wider than the 32-bit hash

/** 32-bit FNV-1a over the whole string. Pure; `Math.imul` keeps it 32-bit. */
function fnv1a32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * A per-entity-unique, `classifySieveTag`-safe discriminator — a hash of the
 * WHOLE entity id.
 *
 * It hashes rather than slices because NO positional slice of an entity id is
 * identifying (#572). Three id shapes reach this function and two put shared or
 * low-entropy data in the tail:
 *   - `n_<slug>_<Date.now()>`      (plant-store.tsx addEntityAndPersist) — the tail
 *     is a millisecond clock, so an 8-char slice wraps every 1e8 ms ≈ 27.8 h.
 *   - `n_<stage>[_<i>]-<plant uuid>` (generatePlant + twin/plants_generate.go:190,
 *     which stamps EVERY entity of a plant with the same `-<plantID>` suffix) — so
 *     a tail slice is IDENTICAL for every entity in one plant. Deterministic
 *     collision, not a rare one.
 *   - a bare uuid — the only shape a tail slice ever handled correctly.
 *
 * Deterministic and stable: the same entity id always yields the same disc, which
 * is what keeps already-provisioned shakers on their existing tag names.
 */
export function sieveDisc(entity: { id: string }): string {
  let n = fnv1a32(entity.id);
  let out = "";
  for (let i = 0; i < DISC_LEN; i++) {
    out = DISC_ALPHABET[n % DISC_ALPHABET.length] + out;
    n = Math.floor(n / DISC_ALPHABET.length);
  }
  return out;
}

/** The datapoint name for one mesh's retained mass (mesh token leads; disc trails). */
export function meshTagName(meshDesignation: string, disc: string): string {
  const label = meshDesignation === "pan" ? "Pan" : `Sieve ${meshDesignation}`;
  return `${label} retained wt · ${disc}`;
}

/** The GFN computed-tag name (fineness role + per-entity unique). */
export function gfnTagName(disc: string): string {
  return `AFS grain fineness · ${disc}`;
}

/** One field/datapoint with the metric the reading is published under. */
export interface SieveFieldLike {
  metric: string;
}

/** Which metric on a target carries which mesh (and the GFN cross-check field). */
export interface SieveFieldMap {
  /** meshDesignation → the manual-tag metric to publish the mass under. */
  byMesh: Record<string, string>;
  /** The GFN/fineness metric, if the target carries one (never published — computed). */
  finenessMetric?: string;
}

/**
 * Classify a target's fields into the mesh mass metrics + the GFN metric, using
 * the SAME `classifySieveTag` grammar the report reads with — so capture and
 * report agree by construction regardless of the exact provisioned names.
 */
export function mapSieveFields(fields: SieveFieldLike[]): SieveFieldMap {
  const byMesh: Record<string, string> = {};
  let finenessMetric: string | undefined;
  for (const f of fields) {
    const role = classifySieveTag(f.metric);
    if (!role) continue;
    if (role.kind === "fineness") finenessMetric = f.metric;
    else byMesh[role.mesh] = f.metric;
  }
  return { byMesh, finenessMetric };
}

/** True when a target carries a recognisable sieve mesh grid (≥1 mesh field). */
export function hasSieveFields(fields: SieveFieldLike[]): boolean {
  return fields.some((f) => classifySieveTag(f.metric)?.kind === "sieve");
}

/**
 * Build the readings to publish for one saved test: every provisioned mesh field,
 * ZERO-FILLED (a blank mesh publishes 0), so the GFN computed tag always sees all
 * its inputs. The GFN/fineness field is never published — the worker derives it.
 */
export function buildSieveReadings(
  fieldMap: SieveFieldMap,
  masses: Record<string, number>,
): ReadingInput[] {
  const out: ReadingInput[] = [];
  for (const step of AFS_US_SERIES) {
    const metric = fieldMap.byMesh[step.meshDesignation];
    if (!metric) continue; // this mesh isn't provisioned on the target
    const m = masses[step.meshDesignation];
    out.push({ metric, value: Number.isFinite(m) ? Number(m) : 0 });
  }
  return out;
}

/** A binding row (subset) the report reads to reconstruct a stored sieve sheet. */
export interface SieveBindingLike {
  tag_id: string;
  tag_name: string;
  /**
   * The bound tag's source (`TwinBinding.source` — 'sensor'|'computed'|'manual'|'ble').
   * Optional so name-only callers still type-check; when it IS present it
   * discriminates OUR derived GFN from a machine-reported fineness reading.
   */
  source?: string;
}

/**
 * Does this binding carry the shaker's DERIVED GFN?
 *
 * Name grammar alone is not enough: the shipped VSync/VAFS templates publish a
 * `Fineness No.` SENSOR tag, which classifies as `fineness` but is a machine
 * reading, not the computed tag this module provisions. Treating one as the other
 * makes an instrumented entity look "already provisioned" and suppresses its GFN.
 *
 * A missing `source` falls back to grammar — no worse than the pre-#573 behaviour
 * for name-only callers, and strictly better whenever the field is present.
 */
function isDerivedFineness(b: SieveBindingLike): boolean {
  return b.source === undefined || b.source === "computed";
}

/** Partition an entity's bindings into its mesh mass tags + the derived GFN tag. */
export function partitionSieveBindings(bindings: SieveBindingLike[]): {
  meshBindings: { mesh: string; tagId: string }[];
  finenessTagId?: string;
} {
  const meshBindings: { mesh: string; tagId: string }[] = [];
  let finenessTagId: string | undefined;
  for (const b of bindings) {
    const role = classifySieveTag(b.tag_name);
    if (!role) continue;
    if (role.kind === "fineness") {
      if (isDerivedFineness(b)) finenessTagId = b.tag_id;
    } else meshBindings.push({ mesh: role.mesh, tagId: b.tag_id });
  }
  return { meshBindings, finenessTagId };
}

/** How many mesh datapoints a fully provisioned AFS shaker carries (12). */
export const SIEVE_MESH_COUNT = AFS_US_SERIES.length;

/** What a given entity's sieve provisioning currently looks like. */
export interface SieveProvisionState {
  /** meshDesignation → bound tag id, deduped (one mesh counts once). */
  meshTagIdByMesh: Record<string, string>;
  /** Distinct meshes bound. */
  meshCount: number;
  /** The bound derived-GFN tag id, if any. */
  finenessTagId?: string;
  /** Any sieve datapoint at all — this entity is, or was meant to be, a shaker. */
  present: boolean;
  /** All 12 meshes AND the GFN. THE gate: `provisionSieveShaker` no-ops iff this. */
  complete: boolean;
}

/**
 * Read an entity's sieve provisioning state from its FULL binding list.
 *
 * ONE definition, shared by `provisionSieveShaker` (as its early-return) and the
 * Manual-entry panel's repair button (#573) — so the button can never hide on a
 * state the provisioning function would still fix, because they are the same
 * boolean. Pass the UNFILTERED bindings: the GFN is `source='computed'` and never
 * appears in the manual/ble subset the panel renders.
 */
export function sieveProvisionState(bindings: SieveBindingLike[]): SieveProvisionState {
  const { meshBindings, finenessTagId } = partitionSieveBindings(bindings);
  const meshTagIdByMesh: Record<string, string> = {};
  for (const b of meshBindings) {
    if (!meshTagIdByMesh[b.mesh]) meshTagIdByMesh[b.mesh] = b.tagId;
  }
  const meshCount = Object.keys(meshTagIdByMesh).length;
  return {
    meshTagIdByMesh,
    meshCount,
    finenessTagId,
    present: meshCount > 0 || Boolean(finenessTagId),
    complete: meshCount >= SIEVE_MESH_COUNT && Boolean(finenessTagId),
  };
}

/**
 * Build the GFN computed-tag create body from the provisioned mesh tag ids
 * (keyed by meshDesignation). Expression is the AFS-2116 weighted ratio
 * `Σ(multiplierᵢ·massᵢ) / Σ massᵢ` over the 12 mesh inputs — the same number
 * `computeGFN` produces, expressed in math.js.
 */
export function buildGfnComputedTag(
  meshTagIdByMesh: Record<string, string>,
  disc: string,
): ComputedTagCreateBody {
  const inputs: ComputedTagInput[] = [];
  const numParts: string[] = [];
  const denParts: string[] = [];
  for (const step of AFS_US_SERIES) {
    const tagId = meshTagIdByMesh[step.meshDesignation];
    if (!tagId) continue;
    const alias = meshAlias(step.meshDesignation);
    inputs.push({ tagId, alias });
    numParts.push(`${step.afsMultiplier}*${alias}`);
    denParts.push(alias);
  }
  return {
    name: gfnTagName(disc),
    description:
      "AFS Grain Fineness Number — Σ(multiplierᵢ·massᵢ)/Σmassᵢ from the sieve shaker's per-mesh retained mass.",
    outputUnit: GFN_OUTPUT_UNIT,
    outputDataKind: GFN_OUTPUT_DATA_KIND,
    inputs,
    expression: `(${numParts.join(" + ")}) / (${denParts.join(" + ")})`,
    trigger: { kind: "on_input_change" },
    enabled: true,
  };
}
